import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  CheckCircle, ClipboardList, BarChart3, Package, AlertCircle, User,
  Scale, TrendingUp, Loader2, ChevronLeft, FileText, ArrowDown,
  ArrowUp, Users, GitBranch, ShoppingCart, Activity, Clock, ChevronDown,
  ChevronRight, Image as ImageIcon, Layers, Lock,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title,
  Tooltip, Legend, ArcElement, PointElement, LineElement, Filler,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, Filler);

/* ─── Types ─────────────────────────────────────────────────────────────── */
interface PendingOrder {
  id: string;
  orderNumber: string;
  productCode: string;
  productName: string;
  customerName?: string;
  customerCode?: string;
  machineName?: string;
  quantityPlanned: string;
  unit: string;
  dueDate?: string;
  updatedAt?: string;
  lotCode?: string;
}

interface ClosedOrder {
  id: string;
  productionOrderId: string;
  orderNumber: string;
  productCode: string;
  productName: string;
  customerName?: string;
  quantityPlanned?: string;
  unit?: string;
  machineName?: string;
  floorProducedWeight?: string;
  floorWaste?: string;
  floorObservations?: string;
  adminProducedWeight?: string;
  adminWaste?: string;
  adminObservations?: string;
  closedByName?: string;
  closedAt: string;
}

interface PostMortemData {
  order: any;
  weights: {
    byProcess: Array<{ name: string; procesoKg: number; procesoBultos: number; mermaKg: number; mermaBultos: number; terminadoKg: number; terminadoBultos: number; isPT: boolean }>;
  };
  routes: Array<{
    id: string;
    sequence: number;
    process: string;
    machineName?: string;
    recordType?: string;
    evidenceCount: number;
  }>;
  participants: Array<{
    id: number;
    fullName: string;
    email: string;
    eventCount: string;
    lastActivity: string;
  }>;
  events: Array<{
    id: string;
    processName?: string;
    description: string;
    evidenceType: string;
    evidenceText?: string;
    evidenceUrl?: string;
    occurredAt: string;
    createdByName?: string;
  }>;
  inventory: {
    movements: Array<{
      fecha?: string;
      folio?: string;
      tipoDoc?: string;
      concepto?: string;
      esEntrada: boolean;
      esSalida: boolean;
      almacen?: string;
      empleado?: string;
      codigo?: string;
      descripcion?: string;
      cantidad?: string;
      unidad?: string;
      lote?: string;
      catalogProductName?: string;
    }>;
    comerciaEntradas: number;
    comerciaSalidas: number;
  };
  existingClosure: any | null;
}

interface Analytics {
  summary: {
    totalClosures: number;
    totalFloorProduced: number; totalFloorWaste: number;
    totalAdminProduced: number; totalAdminWaste: number;
    floorEfficiency: string | number; adminEfficiency: string | number;
    totalPlanned: number;
    routeCompletionRate: string | number;
    routeStats: { total: number; withEvidence: number };
    comerciaStats: { totalEntradas: number; totalSalidas: number; movCount: number };
  };
  weeklyData: Array<{ week: string; floorProduced: number; floorWaste: number; adminProduced: number; adminWaste: number; count: number }>;
  recentClosures: Array<{ orderNumber: string; productName: string; customerName?: string; floorProducedWeight?: string; floorWaste?: string; adminProducedWeight?: string; adminWaste?: string; closedAt: string }>;
  weightByProcess: Array<{ name: string; procesoKg: number; procesoBultos: number; mermaKg: number; mermaBultos: number; terminadoKg: number; terminadoBultos: number; isPT: boolean }>;
  topOperators: Array<{ fullName: string; eventCount: string; ordersCount: string }>;
  machineUtilization: Array<{ name: string; floorKg: number; count: number }>;
  byCustomer: Array<{ name: string; planned: number; floorProduced: number; count: number }>;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const apiGet = (url: string) =>
  fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmt = (d: string) => new Date(d).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
const fmtKg = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/* ─── Section toggle helper ──────────────────────────────────────────────── */
function Section({ title, icon, defaultOpen = false, badge, children }: {
  title: string; icon: React.ReactNode; defaultOpen?: boolean;
  badge?: string | number; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50 transition-colors">
        <span className="text-gray-500">{icon}</span>
        <span className="font-semibold text-gray-800 text-sm flex-1 text-left">{title}</span>
        {badge !== undefined && (
          <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full mr-2">{badge}</span>
        )}
        {open ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>
      {open && <div className="border-t border-gray-100">{children}</div>}
    </div>
  );
}

/* ─── Post-Mortem Panel ──────────────────────────────────────────────────── */
function PostMortemPanel({
  orderId, orderNumber, onBack, onClosed,
}: { orderId: string; orderNumber: string; onBack: () => void; onClosed: () => void }) {
  const [data, setData] = useState<PostMortemData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    floorProducedWeight: '', floorWaste: '', floorObservations: '',
    adminProducedWeight: '', adminWaste: '', adminObservations: '',
  });
  // ROT-208: general (not per-process) closure note, persisted on the order
  // itself (notes.closureGeneralObservations) — separate from the piso/admin
  // observations above, which live on the one-time production_closures row.
  const [closureGeneralObservations, setClosureGeneralObservations] = useState('');
  const [savingClosureObservations, setSavingClosureObservations] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet(`/api/production-closures/${orderId}/postmortem`);
      if (r.ok) {
        const json = await r.json();
        setData(json);
        let parsedNotes: any = {};
        try { if (json.order?.notes) parsedNotes = JSON.parse(json.order.notes); } catch {}
        setClosureGeneralObservations(parsedNotes.closureGeneralObservations || '');
      }
    } finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const saveClosureGeneralObservations = async () => {
    setSavingClosureObservations(true);
    try {
      await fetch(`/api/production-orders/${orderId}/closure-notes`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ closureGeneralObservations }),
      });
    } catch (e) {
      console.error('Error saving closure general observations', e);
    } finally {
      setSavingClosureObservations(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await fetch('/api/production-closures', {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ productionOrderId: orderId, ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v || null])) }),
      });
      if (r.ok) { onClosed(); }
      else { const e = await r.json(); alert(e.error || 'Error al cerrar'); }
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-96 gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      <p className="text-gray-500 text-sm">Cargando post-mortem de {orderNumber}…</p>
    </div>
  );

  if (!data) return <div className="p-8 text-center text-red-500">Error cargando datos</div>;

  const { order, weights, routes, participants, events, inventory, existingClosure } = data;
  const planned = parseFloat(order.quantityPlanned) || 0;
  const routesWithEvidence = routes.filter(r => r.evidenceCount > 0).length;
  const comerciaDiff = inventory.comerciaEntradas - inventory.comerciaSalidas;

  return (
    <div className="space-y-5">
      {/* ── Back + Header ── */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ChevronLeft className="h-4 w-4" /> Volver
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            Post-Mortem de Producción
            <span className="font-mono text-blue-700 text-base">{order.orderNumber}</span>
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{order.productName} · {order.customerName}</p>
        </div>
        {existingClosure ? (
          <span className="flex items-center gap-1.5 bg-green-100 text-green-800 text-xs font-semibold px-3 py-1.5 rounded-full">
            <CheckCircle className="h-3.5 w-3.5" /> Cerrada {fmtDate(existingClosure.closedAt)}
          </span>
        ) : (
          <Button onClick={() => setShowForm(v => !v)} className="bg-green-600 hover:bg-green-700 text-sm">
            <CheckCircle className="h-4 w-4 mr-1.5" />
            {showForm ? 'Cancelar cierre' : 'Registrar cierre'}
          </Button>
        )}
      </div>

      {/* ── KPI strip ──
          ROT-193 (follow-up): dropped "Prod. Terminado (piso)" and "Peso
          total registrado" — both summed weight ACROSS processes, which
          double/triple-counts the same physical roll as it gets weighed
          again at each process it passes through. Weight now only appears
          broken down per process, in "Análisis de Pesos por Proceso" below. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Planeado</p>
          <p className="text-lg font-bold text-gray-900">{fmtKg(planned)} <span className="text-xs font-normal text-gray-400">{order.unit ?? 'kg'}</span></p>
          {order.lotCode && <p className="text-xs text-gray-400 mt-0.5">Lote: {order.lotCode}</p>}
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Rutas con evidencia</p>
          <p className="text-lg font-bold text-blue-700">{routesWithEvidence} / {routes.length}</p>
          <p className="text-xs text-gray-400 mt-0.5">{weights.byProcess.length} proceso(s) con peso registrado</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <p className="text-xs text-gray-500 mb-1">Comercia (mov.)</p>
          <p className="text-lg font-bold text-purple-700">{inventory.movements.length} mov.</p>
          <p className="text-xs text-gray-400 mt-0.5">
            <span className="text-green-600">↑{fmtKg(inventory.comerciaEntradas)}</span>
            {' · '}
            <span className="text-red-500">↓{fmtKg(inventory.comerciaSalidas)}</span>
          </p>
        </div>
      </div>

      {/* ── General closure observations ──
          ROT-208: order-level (not per-process), always visible/editable
          regardless of closure status — persists on the order itself so it
          keeps showing on a CLOSED order and in its reprint. */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          Observaciones Generales al Cierre de Producción
        </label>
        <textarea
          rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
          value={closureGeneralObservations}
          onChange={e => setClosureGeneralObservations(e.target.value)}
          onBlur={saveClosureGeneralObservations}
          placeholder="Notas generales del cierre de esta orden, visibles en la OP y su reimpresión..."
        />
        {savingClosureObservations && <p className="text-[10px] text-gray-400 mt-1">Guardando...</p>}
      </div>

      {/* ── Closure form ── */}
      {showForm && !existingClosure && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-green-800 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> Datos de Cierre
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Piso</p>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Peso producido (kg)</label>
                <input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.floorProducedWeight} onChange={e => setForm(f => ({ ...f, floorProducedWeight: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Merma (kg)</label>
                <input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.floorWaste} onChange={e => setForm(f => ({ ...f, floorWaste: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Observaciones piso</label>
                <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" value={form.floorObservations} onChange={e => setForm(f => ({ ...f, floorObservations: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Administración</p>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Peso producido (kg)</label>
                <input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.adminProducedWeight} onChange={e => setForm(f => ({ ...f, adminProducedWeight: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Merma (kg)</label>
                <input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.adminWaste} onChange={e => setForm(f => ({ ...f, adminWaste: e.target.value }))} placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Observaciones admin</label>
                <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none" value={form.adminObservations} onChange={e => setForm(f => ({ ...f, adminObservations: e.target.value }))} />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Confirmar cierre
            </Button>
          </div>
        </div>
      )}

      {/* If already closed, show closure data summary */}
      {existingClosure && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h3 className="font-semibold text-green-800 text-sm mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" /> Datos registrados en cierre
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><p className="text-xs text-gray-500">Peso piso</p><p className="font-semibold text-gray-800">{existingClosure.floorProducedWeight ?? '-'} kg</p></div>
            <div><p className="text-xs text-gray-500">Merma piso</p><p className="font-semibold text-red-600">{existingClosure.floorWaste ?? '-'} kg</p></div>
            <div><p className="text-xs text-gray-500">Peso admin</p><p className="font-semibold text-gray-800">{existingClosure.adminProducedWeight ?? '-'} kg</p></div>
            <div><p className="text-xs text-gray-500">Merma admin</p><p className="font-semibold text-red-600">{existingClosure.adminWaste ?? '-'} kg</p></div>
          </div>
          {(existingClosure.floorObservations || existingClosure.adminObservations) && (
            <div className="mt-3 text-xs text-gray-600 space-y-1">
              {existingClosure.floorObservations && <p><strong>Obs. piso:</strong> {existingClosure.floorObservations}</p>}
              {existingClosure.adminObservations && <p><strong>Obs. admin:</strong> {existingClosure.adminObservations}</p>}
            </div>
          )}
        </div>
      )}

      {/* ── Weight analysis ── */}
      {/* ROT-193 (follow-up): no cross-process total here anymore — the same
          roll is weighed again at every process it passes through, so
          adding processes together double/triple-counts it. Each process
          stands on its own: its own proceso kg and its own merma kg
          (Producto en merma and sub-reasons). ROT-192: bars are scaled
          against Planeado (not against each other) so the reader can see
          at a glance how much of the planned quantity each process/merma
          represents — matching the reference "Análisis de Pesos por
          procesos" bar chart. */}
      <Section title="Análisis de Pesos por Proceso" icon={<Scale className="h-4 w-4" />} defaultOpen badge={`${weights.byProcess.length} proceso(s)`}>
        <div className="p-5 space-y-3">
          {planned > 0 && (
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="font-semibold text-blue-800">Planeado</span>
                <span className="font-mono font-semibold text-blue-800">{fmtKg(planned)} Kg</span>
              </div>
              <div className="w-full bg-blue-100 rounded h-6" />
            </div>
          )}
          {weights.byProcess.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin registros de peso en la bitácora</p>
          ) : weights.byProcess.map((c, i) => {
            const procesoPctReal = planned > 0 ? (c.procesoKg / planned) * 100 : 0;
            const mermaPctReal = planned > 0 ? (c.mermaKg / planned) * 100 : 0;
            const terminadoPctReal = planned > 0 ? (c.terminadoKg / planned) * 100 : 0;
            const procesoPct = Math.min(procesoPctReal, 100);
            const mermaPct = Math.min(mermaPctReal, 100);
            const terminadoPct = Math.min(terminadoPctReal, 100);
            return (
              <div key={i} className="space-y-1.5">
                <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                  {c.isPT && <span className="text-green-600 text-xs font-bold flex-shrink-0">PT</span>}
                  {c.name}
                </p>
                {c.procesoKg > 0 && (
                  <div>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="font-medium text-green-800">{c.name}</span>
                      <span className="font-mono text-green-800">{c.procesoBultos} Bultos / {fmtKg(c.procesoKg)} Kg{planned > 0 && ` · ${procesoPctReal.toFixed(1)}%`}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded h-6 overflow-hidden">
                      <div className="h-6 rounded bg-green-200" style={{ width: `${procesoPct}%` }} />
                    </div>
                  </div>
                )}
                {c.mermaKg > 0 && (
                  <div>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="font-medium text-amber-800">Merma</span>
                      <span className="font-mono text-amber-800">{c.mermaBultos} bultos / {fmtKg(c.mermaKg)} Kg{planned > 0 && ` · ${mermaPctReal.toFixed(1)}%`}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded h-6 overflow-hidden">
                      <div className="h-6 rounded bg-amber-200" style={{ width: `${mermaPct}%` }} />
                    </div>
                  </div>
                )}
                {c.terminadoKg > 0 && (
                  <div>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="font-medium text-emerald-800">Producto Terminado</span>
                      <span className="font-mono text-emerald-800">{c.terminadoBultos} Bultos / {fmtKg(c.terminadoKg)} Kg{planned > 0 && ` · ${terminadoPctReal.toFixed(1)}%`}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded h-6 overflow-hidden">
                      <div className="h-6 rounded bg-emerald-300" style={{ width: `${terminadoPct}%` }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* ── Comercia comparison ── */}
      <Section title="Movimientos de Inventario (Comercia)" icon={<ShoppingCart className="h-4 w-4" />} badge={inventory.movements.length}>
        <div className="p-5 space-y-4">
          {/* Balance summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <ArrowDown className="h-4 w-4 text-green-600 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Entradas</p>
              <p className="font-bold text-green-700">{fmtKg(inventory.comerciaEntradas)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <ArrowUp className="h-4 w-4 text-red-500 mx-auto mb-1" />
              <p className="text-xs text-gray-500">Salidas</p>
              <p className="font-bold text-red-600">{fmtKg(inventory.comerciaSalidas)}</p>
            </div>
            <div className={`rounded-lg p-3 text-center ${comerciaDiff >= 0 ? 'bg-blue-50' : 'bg-amber-50'}`}>
              <Activity className={`h-4 w-4 mx-auto mb-1 ${comerciaDiff >= 0 ? 'text-blue-600' : 'text-amber-600'}`} />
              <p className="text-xs text-gray-500">Diferencia</p>
              <p className={`font-bold ${comerciaDiff >= 0 ? 'text-blue-700' : 'text-amber-700'}`}>{comerciaDiff >= 0 ? '+' : ''}{fmtKg(comerciaDiff)}</p>
            </div>
          </div>

          {inventory.movements.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-2">Sin movimientos de Comercia registrados</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 text-left text-gray-500 font-medium">Fecha</th>
                    <th className="px-2 py-2 text-left text-gray-500 font-medium">Folio</th>
                    <th className="px-2 py-2 text-left text-gray-500 font-medium">Concepto</th>
                    <th className="px-2 py-2 text-left text-gray-500 font-medium">Descripción</th>
                    <th className="px-2 py-2 text-center text-gray-500 font-medium">E/S</th>
                    <th className="px-2 py-2 text-right text-gray-500 font-medium">Cantidad</th>
                    <th className="px-2 py-2 text-left text-gray-500 font-medium">Unidad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {inventory.movements.map((m, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-600">{m.fecha ? fmtDate(m.fecha) : '-'}</td>
                      <td className="px-2 py-1.5 font-mono text-gray-700">{m.folio ?? '-'}</td>
                      <td className="px-2 py-1.5 text-gray-600">{m.concepto ?? '-'}</td>
                      <td className="px-2 py-1.5 text-gray-800 max-w-xs truncate">{m.catalogProductName || m.descripcion || '-'}</td>
                      <td className="px-2 py-1.5 text-center">
                        {m.esEntrada ? (
                          <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs font-medium">E</span>
                        ) : m.esSalida ? (
                          <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-xs font-medium">S</span>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-1.5 text-right font-semibold text-gray-800 tabular-nums">
                        {m.cantidad ? parseFloat(m.cantidad).toLocaleString('es-MX', { minimumFractionDigits: 2 }) : '-'}
                      </td>
                      <td className="px-2 py-1.5 text-gray-500">{m.unidad ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </Section>

      {/* ── Manufacturing routes ── */}
      <Section title="Ruta de Fabricación" icon={<GitBranch className="h-4 w-4" />} badge={`${routesWithEvidence}/${routes.length} con evidencia`}>
        <div className="p-5">
          {routes.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin ruta de fabricación definida</p>
          ) : (
            <div className="space-y-2">
              {routes.map((r) => (
                <div key={r.id} className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${r.evidenceCount > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {r.evidenceCount > 0 ? <CheckCircle className="h-4 w-4" /> : r.sequence}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 font-medium truncate">{r.process}</p>
                    <p className="text-xs text-gray-400">{r.machineName || 'Sin máquina'} · {r.recordType || 'TEXTO'}</p>
                  </div>
                  {r.evidenceCount > 0 ? (
                    <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                      {r.recordType === 'IMAGEN' ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                      {r.evidenceCount} evidencia{r.evidenceCount !== 1 ? 's' : ''}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400 bg-gray-50 rounded-full px-2 py-0.5">Sin evidencia</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* ── Participants ── */}
      <Section title="Equipo Participante" icon={<Users className="h-4 w-4" />} badge={participants.length}>
        <div className="p-5">
          {participants.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin participantes registrados</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {participants.map((p, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.fullName}</p>
                    <p className="text-xs text-gray-400">{p.eventCount} registro{Number(p.eventCount) !== 1 ? 's' : ''} · último: {fmtDate(p.lastActivity)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Section>

      {/* ── Bitácora ── */}
      <Section title="Bitácora de Producción" icon={<FileText className="h-4 w-4" />} badge={`últimos ${events.length}`}>
        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
          {events.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">Sin eventos registrados</p>
          ) : events.map((e) => {
            const isWeight = e.evidenceText && /peso:/i.test(e.evidenceText);
            return (
              <div key={e.id} className="flex gap-3 items-start text-sm">
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isWeight ? 'bg-green-500' : e.evidenceType === 'IMAGE' ? 'bg-purple-400' : 'bg-blue-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-gray-500 text-xs tabular-nums">{fmt(e.occurredAt)}</span>
                    {e.processName && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{e.processName}</span>}
                    {e.createdByName && <span className="text-xs text-gray-400">{e.createdByName}</span>}
                  </div>
                  <p className="text-gray-700 mt-0.5 break-words">{e.description}</p>
                  {e.evidenceText && <p className="text-xs text-gray-500 mt-0.5 italic line-clamp-2">{e.evidenceText}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function ProductionClosure() {
  const { user } = useAuth();
  const hasClosuresPermission = user?.permissions?.includes('closures:manage') ?? false;

  const [activeTab, setActiveTab] = useState<'closure' | 'analytics'>('closure');
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [closedOrders, setClosedOrders] = useState<ClosedOrder[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedForPostMortem, setSelectedForPostMortem] = useState<{ id: string; orderNumber: string } | null>(null);

  const fetchPending  = async () => { const r = await apiGet('/api/production-closures/pending-orders'); if (r.ok) setPendingOrders(await r.json()); };
  const fetchClosed   = async () => { const r = await apiGet('/api/production-closures'); if (r.ok) setClosedOrders(await r.json()); };
  const fetchAnalytics = async () => { const r = await apiGet('/api/production-closures/analytics'); if (r.ok) setAnalytics(await r.json()); };

  const reload = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchPending(), fetchClosed(), fetchAnalytics()]);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (!hasClosuresPermission) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center">
        <div className="bg-red-50 border border-red-200 rounded-full p-6">
          <Lock className="h-12 w-12 text-red-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800">Acceso restringido</h2>
        <p className="text-gray-500 max-w-sm">
          No tienes permiso para ver el módulo de Cierre de Producción.<br />
          Contacta a tu administrador para solicitar el permiso <span className="font-mono text-sm bg-gray-100 px-1 rounded">closures:manage</span>.
        </p>
      </div>
    );
  }

  const handleClosed = async () => {
    setSelectedForPostMortem(null);
    await reload();
  };

  const weeklyChart = analytics?.weeklyData ? {
    labels: analytics.weeklyData.map(w => new Date(w.week).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })),
    datasets: [
      { label: 'Piso', data: analytics.weeklyData.map(w => w.floorProduced), backgroundColor: 'rgba(34,197,94,0.7)', borderColor: 'rgb(34,197,94)', borderWidth: 2 },
      { label: 'Merma', data: analytics.weeklyData.map(w => w.floorWaste + w.adminWaste), backgroundColor: 'rgba(239,68,68,0.7)', borderColor: 'rgb(239,68,68)', borderWidth: 2 },
    ],
  } : null;

  const _trendChart = analytics?.weeklyData ? {
    labels: analytics.weeklyData.map(w => new Date(w.week).toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })),
    datasets: [
      { label: 'Producción', data: analytics.weeklyData.map(w => w.floorProduced + w.adminProduced), borderColor: 'rgb(34,197,94)', backgroundColor: 'rgba(34,197,94,0.1)', fill: true, tension: 0.4 },
      { label: 'Merma', data: analytics.weeklyData.map(w => w.floorWaste + w.adminWaste), borderColor: 'rgb(239,68,68)', backgroundColor: 'rgba(239,68,68,0.1)', fill: true, tension: 0.4 },
    ],
  } : null;
  void _trendChart;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      <span className="ml-2 text-gray-600">Cargando…</span>
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cierre de Producción</h1>
        <p className="text-gray-500 mt-1 text-sm">Post-mortem inteligente por orden — cotejo de pesos, ruta, equipo y Comercia</p>
      </div>

      {/* If viewing post-mortem, show full panel */}
      {selectedForPostMortem ? (
        <PostMortemPanel
          orderId={selectedForPostMortem.id}
          orderNumber={selectedForPostMortem.orderNumber}
          onBack={() => setSelectedForPostMortem(null)}
          onClosed={handleClosed}
        />
      ) : (
        <>
          {/* Tabs */}
          <div className="flex gap-4 border-b border-gray-200">
            {[
              { id: 'closure', label: 'Cierre de Órdenes', icon: <ClipboardList className="h-4 w-4" /> },
              { id: 'analytics', label: 'Analítica', icon: <BarChart3 className="h-4 w-4" /> },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                className={`pb-3 px-4 font-medium text-sm transition-colors flex items-center gap-1.5 ${activeTab === tab.id ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
                {tab.icon}{tab.label}
              </button>
            ))}
          </div>

          {/* ── Closure tab ── */}
          {activeTab === 'closure' && (
            <div className="space-y-6">
              {/* Pending */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 bg-amber-50 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <h2 className="font-semibold text-amber-800">Pendientes de Cierre</h2>
                  <span className="ml-auto bg-amber-200 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full">{pendingOrders.length}</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {pendingOrders.length === 0 ? (
                    <div className="p-10 text-center">
                      <CheckCircle className="h-10 w-10 mx-auto text-green-500 mb-2" />
                      <p className="text-gray-500 text-sm">No hay órdenes pendientes de cierre</p>
                    </div>
                  ) : pendingOrders.map(order => (
                    <div key={order.id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm bg-blue-100 text-blue-800 px-2 py-0.5 rounded">{order.orderNumber}</span>
                            <span className="font-medium text-gray-900 truncate">{order.productName}</span>
                            {order.customerCode === 'MRP' && (
                              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded flex items-center gap-1"><Layers className="h-3 w-3" />MRP</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                            {order.customerName && <span className="flex items-center gap-1"><User className="h-3 w-3" />{order.customerName}</span>}
                            <span className="flex items-center gap-1"><Package className="h-3 w-3" />{order.quantityPlanned} {order.unit}</span>
                            {order.machineName && <span className="flex items-center gap-1"><Activity className="h-3 w-3" />{order.machineName}</span>}
                            {order.dueDate && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(order.dueDate)}</span>}
                          </div>
                        </div>
                        <Button
                          onClick={() => setSelectedForPostMortem({ id: order.id, orderNumber: order.orderNumber })}
                          className="bg-blue-600 hover:bg-blue-700 text-sm flex-shrink-0"
                          size="sm"
                        >
                          <Activity className="h-3.5 w-3.5 mr-1.5" />
                          Ver post-mortem
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Closed */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 bg-green-50 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <h2 className="font-semibold text-green-800">Órdenes Cerradas</h2>
                  <span className="ml-auto bg-green-200 text-green-800 text-xs font-bold px-2 py-0.5 rounded-full">{closedOrders.length}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orden</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Planeado</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Piso (kg/merma)</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Admin (kg/merma)</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cerrado por</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {closedOrders.length === 0 ? (
                        <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No hay órdenes cerradas</td></tr>
                      ) : closedOrders.map(o => (
                        <tr key={o.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-xs">{o.orderNumber}</td>
                          <td className="px-4 py-3 max-w-xs truncate">{o.productName}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{o.customerName ?? '-'}</td>
                          <td className="px-4 py-3 text-center text-xs tabular-nums">{o.quantityPlanned ?? '-'} {o.unit ?? ''}</td>
                          <td className="px-4 py-3 text-center text-xs tabular-nums">
                            <span className="text-green-600 font-medium">{o.floorProducedWeight ?? '-'}</span>
                            <span className="mx-1 text-gray-300">/</span>
                            <span className="text-red-500">{o.floorWaste ?? '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-center text-xs tabular-nums">
                            <span className="text-blue-600 font-medium">{o.adminProducedWeight ?? '-'}</span>
                            <span className="mx-1 text-gray-300">/</span>
                            <span className="text-orange-500">{o.adminWaste ?? '-'}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">{o.closedByName ?? '-'}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{fmt(o.closedAt)}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedForPostMortem({ id: o.productionOrderId, orderNumber: o.orderNumber })}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <Activity className="h-3 w-3" />Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── Analytics tab ── */}
          {activeTab === 'analytics' && analytics && (
            <div className="space-y-6">

              {/* ── Row 1: KPI cards (8 tiles) ── */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  {
                    label: 'Órdenes cerradas', value: analytics.summary.totalClosures,
                    sub: 'total históricas',
                    icon: <CheckCircle className="h-5 w-5 text-blue-600" />, bg: 'bg-blue-50', color: 'text-gray-900',
                  },
                  {
                    label: 'Prod. piso (cierre)', value: `${analytics.summary.totalFloorProduced.toFixed(1)} kg`,
                    sub: `Merma: ${analytics.summary.totalFloorWaste.toFixed(1)} kg`,
                    icon: <Package className="h-5 w-5 text-teal-600" />, bg: 'bg-teal-50', color: 'text-teal-700',
                  },
                  {
                    label: 'Prod. admin (cierre)', value: `${analytics.summary.totalAdminProduced.toFixed(1)} kg`,
                    sub: `Merma: ${analytics.summary.totalAdminWaste.toFixed(1)} kg`,
                    icon: <Scale className="h-5 w-5 text-blue-600" />, bg: 'bg-blue-50', color: 'text-blue-700',
                  },
                  {
                    label: 'Eficiencia piso', value: `${analytics.summary.floorEfficiency}%`,
                    sub: `Admin: ${analytics.summary.adminEfficiency}%`,
                    icon: <TrendingUp className="h-5 w-5 text-purple-600" />, bg: 'bg-purple-50', color: 'text-purple-700',
                  },
                  {
                    label: 'Rutas con evidencia', value: `${analytics.summary.routeCompletionRate}%`,
                    sub: `${analytics.summary.routeStats.withEvidence}/${analytics.summary.routeStats.total} pasos`,
                    icon: <GitBranch className="h-5 w-5 text-indigo-600" />, bg: 'bg-indigo-50', color: 'text-indigo-700',
                  },
                  {
                    label: 'Movs. Comercia', value: analytics.summary.comerciaStats.movCount,
                    sub: `E: ${analytics.summary.comerciaStats.totalEntradas.toFixed(0)} / S: ${analytics.summary.comerciaStats.totalSalidas.toFixed(0)}`,
                    icon: <ShoppingCart className="h-5 w-5 text-amber-600" />, bg: 'bg-amber-50', color: 'text-amber-700',
                  },
                ].map((card, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex items-start gap-3">
                    <div className={`p-2.5 rounded-lg flex-shrink-0 mt-0.5 ${card.bg}`}>{card.icon}</div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 leading-tight">{card.label}</p>
                      <p className={`text-base font-bold mt-0.5 ${card.color}`}>{card.value}</p>
                      {card.sub && <p className="text-xs text-gray-400 leading-snug mt-0.5">{card.sub}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Row 2: Weekly chart + Weight by concept ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-blue-500" />Producción vs Merma por Semana
                  </h3>
                  {weeklyChart
                    ? <Bar data={weeklyChart} options={{ responsive: true, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true } } }} />
                    : <p className="text-center text-gray-400 py-16 text-sm">Sin datos semanales</p>}
                </div>

                {/* Peso por proceso (bitácora) */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm flex items-center gap-2">
                    <Scale className="h-4 w-4 text-green-500" />Peso por Proceso (Bitácora Acumulada)
                  </h3>
                  {analytics.weightByProcess.length === 0 ? (
                    <p className="text-center text-gray-400 py-16 text-sm">Sin registros de peso en la bitácora</p>
                  ) : (
                    // ROT-193 (follow-up): no total across processes — the same
                    // roll gets weighed again at each process, so a sum here
                    // would double/triple-count it (this dashboard sums a given
                    // PROCESS across every closed order, which is legitimate;
                    // it just never sums across DIFFERENT processes). Merma
                    // shown per process is that process's own share.
                    <div className="space-y-2">
                      {analytics.weightByProcess.slice(0, 8).map((c, i) => {
                        const totalKg = c.procesoKg + c.mermaKg + c.terminadoKg;
                        const mermaPct = totalKg > 0 ? (c.mermaKg / totalKg) * 100 : 0;
                        return (
                          <div key={i}>
                            <div className="flex items-center gap-1">
                              {c.isPT && <span className="text-green-600 text-[10px] font-bold flex-shrink-0 bg-green-100 px-1 rounded">PT</span>}
                              <span className="text-xs text-gray-700 truncate flex-1">{c.name}</span>
                              <span className="text-xs font-semibold text-gray-800 tabular-nums">{c.procesoBultos} bultos / {c.procesoKg.toFixed(1)} kg</span>
                            </div>
                            {c.mermaKg > 0 && (
                              <div className="flex items-center justify-between text-[11px]">
                                <span className="text-red-600">Merma: {c.mermaBultos} bultos / {c.mermaKg.toFixed(1)} kg</span>
                                <span className="text-gray-400">{mermaPct.toFixed(1)}%</span>
                              </div>
                            )}
                            {c.terminadoKg > 0 && (
                              <div className="flex items-center justify-between text-[11px]">
                                <span className="text-emerald-700">Terminado: {c.terminadoBultos} bultos / {c.terminadoKg.toFixed(1)} kg</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Row 3: Top operators + Machine utilization ── */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Top Operators */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm flex items-center gap-2">
                    <User className="h-4 w-4 text-indigo-500" />Top Operadores
                  </h3>
                  {analytics.topOperators.length === 0 ? (
                    <p className="text-center text-gray-400 py-8 text-sm">Sin datos de operadores</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.topOperators.map((op, i) => {
                        const maxEvt = Number(analytics.topOperators[0]?.eventCount) || 1;
                        const pct = (Number(op.eventCount) / maxEvt) * 100;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-gray-400 w-4 text-right">{i + 1}</span>
                            <span className="text-xs text-gray-700 w-36 truncate font-medium">{op.fullName}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div className="h-2 rounded-full bg-indigo-400" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs tabular-nums text-gray-700 font-semibold w-12 text-right">{op.eventCount} evt</span>
                            <span className="text-xs text-gray-400 w-14 text-right">{op.ordersCount} órds</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Machine utilization */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm flex items-center gap-2">
                    <Activity className="h-4 w-4 text-teal-500" />Utilización por Máquina
                  </h3>
                  {analytics.machineUtilization.length === 0 ? (
                    <p className="text-center text-gray-400 py-8 text-sm">Sin datos de máquinas</p>
                  ) : (
                    <div className="space-y-2">
                      {analytics.machineUtilization.map((m, i) => {
                        const maxKg = analytics.machineUtilization[0]?.floorKg || 1;
                        const pct = (m.floorKg / maxKg) * 100;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-gray-700 w-36 truncate font-medium">{m.name}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div className="h-2 rounded-full bg-teal-400" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs tabular-nums text-gray-700 font-semibold w-20 text-right">{m.floorKg.toFixed(1)} kg</span>
                            <span className="text-xs text-gray-400 w-10 text-right">{m.count} órds</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Row 4: By customer ── */}
              {analytics.byCustomer.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-amber-500" />Producción por Cliente
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          {['Cliente', 'Órdenes', 'Planeado (m)', 'Prod. piso (kg)', 'Cumplimiento'].map(h => (
                            <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {analytics.byCustomer.map((c, i) => {
                          const cumpl = c.planned > 0 ? (c.floorProduced / c.planned * 100) : 0;
                          return (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-900 truncate max-w-xs">{c.name}</td>
                              <td className="px-3 py-2 text-center text-xs text-gray-500">{c.count}</td>
                              <td className="px-3 py-2 tabular-nums text-xs text-gray-700">{c.planned.toFixed(0)}</td>
                              <td className="px-3 py-2 tabular-nums text-xs font-semibold text-green-700">{c.floorProduced.toFixed(1)}</td>
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden min-w-16">
                                    <div className={`h-1.5 rounded-full ${cumpl >= 90 ? 'bg-green-500' : cumpl >= 70 ? 'bg-amber-400' : 'bg-red-400'}`} style={{ width: `${Math.min(cumpl, 100)}%` }} />
                                  </div>
                                  <span className="text-xs tabular-nums w-10 text-right text-gray-600">{cumpl.toFixed(0)}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Row 5: Recent closures ── */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                <h3 className="font-semibold text-gray-800 mb-4 text-sm flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-gray-400" />Últimos cierres
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Orden', 'Producto', 'Cliente', 'Piso kg / merma', 'Admin kg / merma', 'Fecha'].map(h => (
                          <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {analytics.recentClosures.length === 0 ? (
                        <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-400 text-sm">Sin cierres registrados</td></tr>
                      ) : analytics.recentClosures.map((c, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{c.orderNumber}</td>
                          <td className="px-3 py-2 text-sm max-w-xs truncate">{c.productName}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{c.customerName ?? '-'}</td>
                          <td className="px-3 py-2 text-xs tabular-nums">
                            <span className="text-green-600 font-medium">{c.floorProducedWeight ?? '-'}</span>
                            <span className="mx-1 text-gray-300">/</span>
                            <span className="text-red-500">{c.floorWaste ?? '-'}</span>
                          </td>
                          <td className="px-3 py-2 text-xs tabular-nums">
                            <span className="text-blue-600 font-medium">{c.adminProducedWeight ?? '-'}</span>
                            <span className="mx-1 text-gray-300">/</span>
                            <span className="text-orange-500">{c.adminWaste ?? '-'}</span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-500">{fmt(c.closedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          )}
        </>
      )}
    </div>
  );
}
